import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatMonth } from "../lib/format";

const MONTH_SHORT = [
  "jan", "feb", "mar", "apr", "maj", "jun",
  "jul", "aug", "sep", "okt", "nov", "dec",
];

interface MonthPickerProps {
  value: string | null; // YYYY-MM or null for "all"
  availableMonths: string[];
  onChange: (month: string | null) => void;
}

export function MonthPicker({
  value,
  availableMonths,
  onChange,
}: MonthPickerProps) {
  const sorted = useMemo(() => [...availableMonths].sort(), [availableMonths]);
  const availableSet = useMemo(() => new Set(sorted), [sorted]);
  const years = useMemo(
    () => [...new Set(sorted.map((m) => m.slice(0, 4)))].sort(),
    [sorted]
  );

  const currentIdx = value ? sorted.indexOf(value) : -1;
  const fallbackYear = years[years.length - 1] ?? String(new Date().getFullYear());

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(value?.slice(0, 4) ?? fallbackYear);
  const containerRef = useRef<HTMLDivElement>(null);

  const yearIdx = years.indexOf(viewYear);

  function toggleOpen() {
    if (!open) setViewYear(value?.slice(0, 4) ?? fallbackYear);
    setOpen((v) => !v);
  }

  function select(month: string | null) {
    onChange(month);
    setOpen(false);
  }

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function prev() {
    if (currentIdx > 0) {
      onChange(sorted[currentIdx - 1]!);
    }
  }

  function next() {
    if (currentIdx < sorted.length - 1) {
      onChange(sorted[currentIdx + 1]!);
    }
  }

  return (
    <div ref={containerRef} className="relative flex items-center gap-2">
      <Button
        variant={value === null ? "default" : "outline"}
        size="sm"
        onClick={() => select(null)}
      >
        Alla
      </Button>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={prev}
          disabled={currentIdx <= 0}
          aria-label="Föregående månad"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <button
          onClick={toggleOpen}
          aria-haspopup="dialog"
          aria-expanded={open}
          className="flex min-w-[150px] items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium transition-colors hover:bg-muted"
        >
          {value ? formatMonth(value) : "Alla månader"}
          <ChevronDown
            className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={next}
          disabled={currentIdx >= sorted.length - 1}
          aria-label="Nästa månad"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {open && (
        <div
          role="dialog"
          aria-label="Välj månad"
          className="absolute right-0 top-full z-40 mt-2 w-72 rounded-xl border bg-popover p-3 shadow-lg"
        >
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setViewYear(years[yearIdx - 1]!)}
              disabled={yearIdx <= 0}
              aria-label="Föregående år"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold tabular-nums">{viewYear}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setViewYear(years[yearIdx + 1]!)}
              disabled={yearIdx === -1 || yearIdx >= years.length - 1}
              aria-label="Nästa år"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="mt-2 grid grid-cols-4 gap-1">
            {MONTH_SHORT.map((name, i) => {
              const monthKey = `${viewYear}-${String(i + 1).padStart(2, "0")}`;
              const isAvailable = availableSet.has(monthKey);
              const isSelected = value === monthKey;

              return (
                <button
                  key={monthKey}
                  disabled={!isAvailable}
                  onClick={() => select(monthKey)}
                  className={`rounded-md py-2 text-sm font-medium transition-colors ${
                    isSelected
                      ? "bg-primary text-primary-foreground"
                      : isAvailable
                        ? "hover:bg-muted"
                        : "text-muted-foreground/40"
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => select(null)}
            className={`mt-2 w-full rounded-md border py-1.5 text-sm font-medium transition-colors ${
              value === null
                ? "border-primary bg-primary/10 text-primary"
                : "hover:bg-muted"
            }`}
          >
            Alla månader
          </button>
        </div>
      )}
    </div>
  );
}
